import { useCallback, useEffect, useMemo, useState } from "react";
import en from "../locales/en";
import { storageService } from "../services/storageService";
import { getGoalProgress } from "../utils/finance";
import { createId } from "../utils/formatters";

const noop = () => {};

export default function useGoals(currentUser, options = {}) {
  const [goals, setGoals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const currentUserId = currentUser?.id;
  const showNotification = options.showNotification || noop;
  const messages = (options.locale || en).notifications;
  const notifyText = useCallback(
    (key, ...args) => {
      const value = messages[key] || en.notifications[key];
      return typeof value === "function" ? value(...args) : value;
    },
    [messages]
  );

  const persistGoals = useCallback(
    async (nextGoals, successMessage) => {
      if (!currentUserId) return [];

      await storageService.saveGoals(currentUserId, nextGoals);
      setGoals(nextGoals);

      if (successMessage) {
        showNotification("success", successMessage);
      }

      return nextGoals;
    },
    [currentUserId, showNotification]
  );

  const loadGoals = useCallback(async () => {
    if (!currentUserId) {
      setGoals([]);
      return;
    }

    setIsLoading(true);

    try {
      setGoals(await storageService.getGoals(currentUserId));
    } catch (error) {
      showNotification("error", error.message || notifyText("goalsLoadFailed"));
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId, notifyText, showNotification]);

  useEffect(() => {
    loadGoals();
  }, [loadGoals]);

  const addGoal = useCallback(
    async (draft) => {
      if (!currentUserId) return;

      setIsLoading(true);

      try {
        const payload = {
          ...draft,
          id: createId(),
          targetAmount: Number(draft.targetAmount),
          currentAmount: Number(draft.currentAmount || 0),
          targetDate: (draft.targetDate instanceof Date ? draft.targetDate : new Date(draft.targetDate)).toISOString(),
          userId: currentUserId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        await persistGoals([payload, ...goals], notifyText("goalSaved"));
      } catch (error) {
        showNotification("error", error.message || notifyText("goalSaveFailed"));
      } finally {
        setIsLoading(false);
      }
    },
    [currentUserId, goals, notifyText, persistGoals, showNotification]
  );

  const updateGoal = useCallback(
    async (goal, draft) => {
      if (!currentUserId || !goal) return;

      setIsLoading(true);

      try {
        const payload = {
          ...draft,
          id: goal.id,
          targetAmount: Number(draft.targetAmount),
          currentAmount: Number(draft.currentAmount || 0),
          targetDate: (draft.targetDate instanceof Date ? draft.targetDate : new Date(draft.targetDate)).toISOString(),
          userId: currentUserId,
          createdAt: goal.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        const nextGoals = goals.map((item) => (item.id === goal.id ? payload : item));

        await persistGoals(nextGoals, notifyText("goalUpdated"));
      } catch (error) {
        showNotification("error", error.message || notifyText("goalUpdateFailed"));
      } finally {
        setIsLoading(false);
      }
    },
    [currentUserId, goals, notifyText, persistGoals, showNotification]
  );

  const restoreGoal = useCallback(
    async (goal, baseGoals = goals) => {
      setIsLoading(true);

      try {
        await persistGoals([goal, ...baseGoals], notifyText("goalRestored"));
      } catch (error) {
        showNotification("error", error.message || notifyText("goalRestoreFailed"));
      } finally {
        setIsLoading(false);
      }
    },
    [goals, notifyText, persistGoals, showNotification]
  );

  const deleteGoal = useCallback(
    async (goalId) => {
      setIsLoading(true);

      try {
        const deletedGoal = goals.find((goal) => goal.id === goalId);
        if (!deletedGoal) return;

        const nextGoals = goals.filter((goal) => goal.id !== goalId);
        await persistGoals(nextGoals);
        showNotification("success", notifyText("goalDeleted"), {
          actionLabel: notifyText("undo"),
          duration: 5000,
          onAction: () => restoreGoal(deletedGoal, nextGoals)
        });
      } catch (error) {
        showNotification("error", error.message || notifyText("goalDeleteFailed"));
      } finally {
        setIsLoading(false);
      }
    },
    [goals, notifyText, persistGoals, restoreGoal, showNotification]
  );

  const saveGoal = useCallback(
    (draft, editingGoal) => (editingGoal ? updateGoal(editingGoal, draft) : addGoal(draft)),
    [addGoal, updateGoal]
  );

  const reorderGoals = useCallback(
    async (sourceId, targetId) => {
      setIsLoading(true);

      try {
        const nextGoals = [...goals];
        const fromIndex = nextGoals.findIndex((goal) => goal.id === sourceId);
        const toIndex = nextGoals.findIndex((goal) => goal.id === targetId);

        if (fromIndex < 0 || toIndex < 0) return;

        const [movedGoal] = nextGoals.splice(fromIndex, 1);
        nextGoals.splice(toIndex, 0, movedGoal);
        await persistGoals(nextGoals);
      } catch (error) {
        showNotification("error", error.message || notifyText("goalReorderFailed"));
      } finally {
        setIsLoading(false);
      }
    },
    [goals, notifyText, persistGoals, showNotification]
  );

  const resetGoals = useCallback(async () => {
    if (!currentUserId) return;

    setIsLoading(true);

    try {
      await storageService.saveGoals(currentUserId, []);
      setGoals([]);
    } catch (error) {
      showNotification("error", error.message || notifyText("goalsResetFailed"));
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId, notifyText, showNotification]);

  const replaceGoals = useCallback((nextGoals) => {
    setGoals(nextGoals);
  }, []);

  const goalProgress = useMemo(() => getGoalProgress(goals), [goals]);

  return {
    addGoal,
    deleteGoal,
    goalProgress,
    goals,
    isLoading,
    reorderGoals,
    replaceGoals,
    resetGoals,
    restoreGoal,
    saveGoal,
    updateGoal
  };
}
